/**
 * The dashboard's shared pieces: a strip of figures, a list of facts, a panel.
 * Every tab is built from these, so the dashboard reads as one page and a
 * number always says what period it covers.
 */
import { ReactNode, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import type { FleetData, FleetTier } from '../fleetData';
import './kit.css';

/**
 * The period a number covers. `window` follows the time control and carries no
 * tag. Every other scope is tagged beside the number's name, so nobody reads
 * a lifetime total as last week's.
 */
export type Scope = 'window' | 'now' | 'month' | 'last-30d' | 'all-time';

const SCOPE_LABEL: Record<Exclude<Scope, 'window'>, { text: string; title: string }> = {
  now: { text: 'now', title: 'As it stands at the last nightly update. The time window does not apply.' },
  month: { text: 'this month', title: 'The current New Zealand billing month. The time window does not apply.' },
  'last-30d': {
    text: 'last 30 days',
    title: 'The 30 days before the last nightly update. The time window does not apply.',
  },
  'all-time': {
    text: 'all time',
    title: 'Everything on record. This number cannot follow the time window yet.',
  },
};

export function ScopeTag({ scope }: { scope?: Scope }) {
  if (!scope || scope === 'window') return null;
  const { text, title } = SCOPE_LABEL[scope];
  return (
    <span className={`dash-mark dash-mark--${scope}`} title={title}>
      {text}
    </span>
  );
}

/** The mark on a number that is close but not exact. The reason is in its hover text. */
export function ApproxMark({ note }: { note?: string }) {
  return (
    <span className="dash-mark dash-mark--approx" title={note ?? 'This number is close but not exact.'}>
      approx.
    </span>
  );
}

/**
 * What a selector's answer says about a number, beyond the number: the period
 * it covers, if it is exact, and why not. A tile and a row of facts both take
 * these.
 */
export interface FigureMarks {
  scope?: Scope;
  /** The number is close but not exact. It is shown as it is, with a mark beside its name. */
  approx?: boolean;
  /** Why the number is approximate, or what it leaves out. */
  note?: string;
  /** What the number is, shown on hover of its name. */
  hint?: string;
}

/** A number's marks, in the order they read: the period, then "approx.". */
function Marks({ scope, approx, note }: FigureMarks) {
  if (!approx && (!scope || scope === 'window')) return null;
  return (
    <span className="dash-marks">
      <ScopeTag scope={scope} />
      {approx && <ApproxMark note={note} />}
    </span>
  );
}

/** A number's name is hovered for what it means, then for anything its selector says about it. */
const hoverText = (hint?: string, note?: string) => [hint, note].filter(Boolean).join(' ') || undefined;

interface SectionProps {
  title: string;
  /** A short line on the right: what the numbers below are measured over. */
  note?: ReactNode;
  children: ReactNode;
}

/** A labelled part of a tab. A tab needs few of these. */
export function Section({ title, note, children }: SectionProps) {
  return (
    <section className="dash-section">
      <header className="dash-section__head">
        <h2 className="section-label dash-section__title">{title}</h2>
        {note && <div className="dash-section__note">{note}</div>}
      </header>
      {children}
    </section>
  );
}

/**
 * One strip of figures: the few numbers a person came to the tab for, side by
 * side in one frame with a hairline between them. `cols` is the count where
 * there is room; the strip folds to fewer columns as it narrows.
 */
export function KpiGrid({ children, cols = 4 }: { children: ReactNode; cols?: 2 | 3 | 4 | 5 | 6 }) {
  return (
    <div className="dash-strip-box">
      <div className={`dash-strip dash-strip--${cols}`}>{children}</div>
    </div>
  );
}

interface KpiProps extends FigureMarks {
  label: string;
  value: ReactNode;
  /** One quiet line under the value: the parts it is made of, or its basis. */
  sub?: ReactNode;
  /** Colour for the value. Use it for meaning (owed, saved, failed), never for decoration. */
  tone?: 'default' | 'good' | 'warn' | 'bad';
  /** The one figure of the strip a person reads first. It is larger. */
  lead?: boolean;
}

/** One figure of a strip: its name, its number, and what the number is made of. */
export function Kpi({ label, value, sub, tone = 'default', lead, scope, approx, note, hint }: KpiProps) {
  const classes = ['dash-kpi', tone !== 'default' ? `dash-kpi--${tone}` : '', lead ? 'dash-kpi--lead' : '']
    .filter(Boolean)
    .join(' ');
  return (
    <div className={classes}>
      <div className="dash-kpi__label">
        <span className="dash-kpi__name" title={hoverText(hint, note)}>
          {label}
        </span>
        <Marks scope={scope} approx={approx} note={note} />
      </div>
      <div className="dash-kpi__value numeric">{value}</div>
      {sub && <div className="dash-kpi__sub">{sub}</div>}
    </div>
  );
}

/**
 * Secondary figures as a dense list: a name on the left, its number on the
 * right. Put one inside a `Panel`, with a `FactGroup` for each column.
 */
export function Facts({ children, cols = 3 }: { children: ReactNode; cols?: 1 | 2 | 3 }) {
  return <div className={`dash-facts dash-facts--${cols}`}>{children}</div>;
}

/** One column of facts, with an optional small heading above its rows. */
export function FactGroup({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div className="dash-facts__group">
      {title && <h4 className="dash-facts__title">{title}</h4>}
      <dl className="dash-facts__rows">{children}</dl>
    </div>
  );
}

interface FactProps extends FigureMarks {
  label: string;
  value: ReactNode;
  tone?: 'default' | 'good' | 'warn' | 'bad';
}

/** One row of facts. It takes the same marks as a tile. */
export function Fact({ label, value, tone = 'default', scope, approx, note, hint }: FactProps) {
  return (
    <div className="dash-fact">
      <dt className="dash-fact__label">
        <span title={hoverText(hint, note)}>{label}</span>
        <Marks scope={scope} approx={approx} note={note} />
      </dt>
      <dd
        className={
          tone === 'default' ? 'dash-fact__value numeric' : `dash-fact__value dash-fact__value--${tone} numeric`
        }
      >
        {value}
      </dd>
    </div>
  );
}

interface PanelProps {
  title: string;
  /** One quiet line under the title. */
  subtitle?: ReactNode;
  scope?: Scope;
  /** Controls on the right of the title: a toggle, a download. */
  actions?: ReactNode;
  /** No padding around the content. Use it for a table. */
  flush?: boolean;
  children: ReactNode;
}

/** A plain frame with a title: one chart, one table or one list of facts. */
export function Panel({ title, subtitle, scope, actions, flush, children }: PanelProps) {
  return (
    <div className="dash-panel">
      <header className="dash-panel__head">
        <div className="dash-panel__titles">
          <h3 className="dash-panel__title">
            {title}
            <ScopeTag scope={scope} />
          </h3>
          {subtitle && <div className="dash-panel__subtitle">{subtitle}</div>}
        </div>
        {actions && <div className="dash-panel__actions">{actions}</div>}
      </header>
      <div className={flush ? 'dash-panel__body dash-panel__body--flush' : 'dash-panel__body'}>{children}</div>
    </div>
  );
}

/** What a list shows when it has nothing to show. */
export function Empty({ children }: { children: ReactNode }) {
  return <div className="dash-empty">{children}</div>;
}

interface NeedsTierProps {
  fleet: FleetData;
  tiers: FleetTier[];
  /** What is loading, for the person waiting: "Per-person history". */
  what: string;
  children: ReactNode;
}

/**
 * Wraps a part of a page that needs heavier data. It asks for the tiers, shows
 * a quiet line until they arrive, then draws its children.
 */
export function NeedsTier({ fleet, tiers, what, children }: NeedsTierProps) {
  const { ensureTier, hasTiers } = fleet;
  useEffect(() => {
    tiers.forEach((tier) => ensureTier(tier));
    // The tier list is a literal at every call site, so its names stand in for it.
  }, [ensureTier, tiers.join(',')]);

  if (hasTiers(...tiers)) return <>{children}</>;
  const failed = tiers.some((tier) => fleet.tiers[tier] === 'error');
  return (
    <div className="dash-empty">
      {failed ? (
        `${what} did not load. Refresh the page to try again.`
      ) : (
        <>
          <Spinner animation="border" size="sm" className="me-2" />
          Loading {what.toLowerCase()}
        </>
      )}
    </div>
  );
}
